<template>
    <transition
        enter-class="opacity-0"
        enter-active-class="transition-opacity duration-200"
        leave-to-class="opacity-0"
        leave-active-class="transition-opacity duration-200"
    >
        <Layout v-show="ready" :show-background="true"
                container-class="flex flex-col min-h-screen py-10 sm:min-h-0 bg-primary">
            <div v-if="ready" class="w-full max-w-sm px-10 mx-auto">
                <BackButton location="Flow" />
                <div class="flex justify-center mb-8">
                    <img :src="config.service_logo" :alt="config.service_name"
                         class="block w-auto h-12">
                </div>

                <h1 class="mb-8 text-xl font-bold leading-tight text-center text-highlight-100">
                    {{ _('otp_enter_number_title') }}
                </h1>

                <form @submit.prevent="submit">
                    <NumberField
                        class="mb-6"
                        :label="_('otp_number_input')"
                        :validation.sync="valid.otpValue"
                        v-model="otpValue"
                    />

                    <p v-if="displayError" class="px-4 mb-2 text-xs text-center text-error">
                        {{ displayError }}
                    </p>

                    <BaseButton type="button" :disabled="!isValid">
                        {{ _('otp_submit') }}
                    </BaseButton>
                </form>
            </div>

            <Portal to="spinner">
                <div v-if="loading"
                     class="fixed inset-0 flex flex-col items-center justify-center p-8 bg-background-80">
                    <div class="mb-20">
                        <BaseSpinner/>
                    </div>
                </div>
            </Portal>
        </Layout>
    </transition>
</template>

<script>
import { mapGetters } from 'vuex'
import NProgress from 'nprogress'
import Layout from '@/components/Layout.vue'
import BaseButton from '@/components/Base/BaseButton.vue'
import BaseSpinner from '@/components/Base/BaseSpinner.vue'
import NumberField from '@/components/Form/Fields/NumberField.vue'
import BackButton from '@/components/BackButton.vue'
import store from '../store'

export default {
    components: {
        BackButton,
        Layout,
        BaseButton,
        BaseSpinner,
        NumberField,
    },
    data() {
        return {
            otpValue: '',
            displayError: false,
            valid: {
                otpValue: false,
            },
            ready: false,
            loading: false,
            config: '',
        }
    },
    computed: {
        ...mapGetters({
            fetched: 'localisation/fetched',
            _: 'localisation/translate',
            client: 'settings/getClient',
            params: 'settings/get',
            authenticated: 'user/authenticated',
            user: 'user/get',
            provider: 'providers/selected',
            otpParams: 'otp/params',
            otpError: 'otp/error',
        }),
        isValid() {
            return Object.keys(this.valid)
                .every((field) => this.valid[field])
        },
        title() {
            return this.$router.currentRoute.name
        },
        accessToken() {
            return Object.keys(store.getters['user/authenticated']).length
                ? store.getters['user/authenticated'].access_token
                : this.$route.query.access_token
        },
    },
    watch: {
        fetched: {
            immediate: true,
            deep: true,
            handler(fetched) {
                if (fetched) {
                    setTimeout(() => {
                        this.ready = true
                        NProgress.done()
                    }, 1000)
                }
            },
        },
        otpError: {
            immediate: true,
            handler(otpError) {
                if (otpError) {
                    this.loading = false
                    this.displayError = otpError
                }
            },
        },
    },
    created() {
        NProgress.start()
    },
    async mounted() {
        this.config = await import(`@/resources/${this.client}`)
        this.$store.dispatch('otp/resetErrors')

        await this.$store.dispatch('user/get', this.authenticated.access_token)
            .then(() => {
                this.loading = false
            })

        if (this.isUserOTPVerified(this.provider.name.toLowerCase())) {
            return this.$router.push({ name: this.provider.name })
        }

        return this.$store.dispatch('otp/requestToken', {
            msisdn: this.user.phone_number,
            access_token: this.authenticated.access_token,
        }).then(() => {
            this.loading = false
        })
    },
    methods: {
        async submit() {
            if (this.isValid) {
                this.loading = true
                this.$store.dispatch('otp/resetErrors')

                const { phone_number } = this.user

                return this.$store.dispatch('otp/validateToken', {
                    msisdn: phone_number,
                    otp: this.otpValue,
                    ...this.otpParams,
                })
                    .then(() => {
                        const { access_token } = this.authenticated

                        this.$store.dispatch('user/markUserOTPVerified', {
                            company_uuid: this.params.company_uuid,
                            user_uuid: this.user.uuid,
                            service_name: this.provider.name.toLowerCase(),
                            access_token,
                        })

                        this.$router.push({ name: 'Safaricom' })
                    })
                    .catch((err) => {
                        console.log(err)
                        this.$store.dispatch('otp/validationFailed', err)
                        this.loading = false
                    })
            }

            return null;
        },
        isUserOTPVerified(providerName) {
            try {
                const service = this.getUserThirdPartyService(providerName)

                return service !== null
                    ? (service.account.otp.status === 'verified')
                    : false
            } catch (err) {
                // catch missing keys in user service
                return false
            }
        },
        getUserThirdPartyService(serviceName) {
            const entryIndex = this.user.third_party_services.map((entry) => entry.service).indexOf(serviceName)

            return entryIndex !== -1
                ? this.user.third_party_services[entryIndex]
                : null
        },
    },
}
</script>
